import React, { useEffect } from 'react'
import { FormProvider } from './hooks/useForm'
import { FingerprintProvider } from '@fingerprint/react'
import { BotdProvider } from './context/BotdContext'
import { AmpliProvider } from './helpers/amplitude'
import { HistoryListener } from './context/HistoryListener'
import { CookieChoiceProvider } from './hooks/useCookieConsent'
import { SystemStatusProvider } from './context/SystemStatusContext'
import { FPJS_PUBLIC_TOKEN, FPJS_REGION, HOST, IS_PRODUCTION } from './constants/env'
import { ExperimentProvider } from './helpers/amplitude-experiment'
import loadable from '@loadable/component'
import { usePreview } from './context/PreviewContext'
import { initDatadog } from './helpers/datadog'
import { ExperimentsProvider } from './context/ExperimentsContext'

const ClerkLikeChat = React.lazy(() => import('./components/ClerkLikeChat/ClerkLikeChat'))
const ExperimentDevTools = loadable(() =>
  import('./components/ExperimentDevTools/ExperimentDevTools').then((m) => m.ExperimentDevTools)
)

function ConditionalClerkLikeChat() {
  const { isPreview } = usePreview()
  const [shouldRenderChat, setShouldRenderChat] = React.useState(false)

  useEffect(() => {
    if (isPreview || typeof window === 'undefined') {
      return
    }

    const scheduleIdle =
      window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(callback, 2500))
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout
    const idleId = scheduleIdle(() => setShouldRenderChat(true))

    return () => cancelIdle(idleId)
  }, [isPreview])

  if (isPreview || !shouldRenderChat) {
    return null
  }

  return (
    <React.Suspense fallback={null}>
      <ClerkLikeChat />
    </React.Suspense>
  )
}

export type Props = {
  children: React.ReactNode
}

export default function AppProviders({ children }: Props) {
  // Initialize Datadog logging on first load
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const scheduleIdle =
      window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(callback, 2500))
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout
    const idleId = scheduleIdle(() => {
      void initDatadog()
    })

    return () => cancelIdle(idleId)
  }, [])

  return (
    <AppLighthouseProvider>
      <BotdProvider>
        <FormProvider>
          <SystemStatusProvider>
            <CookieChoiceProvider>
              <HistoryListener>
                <AmpliProvider>
                  <ExperimentsProvider>
                    <ExperimentProvider>
                      <React.StrictMode>{children}</React.StrictMode>
                      {!IS_PRODUCTION ? <ExperimentDevTools /> : null}
                      <ConditionalClerkLikeChat />
                    </ExperimentProvider>
                  </ExperimentsProvider>
                </AmpliProvider>
              </HistoryListener>
            </CookieChoiceProvider>
          </SystemStatusProvider>
        </FormProvider>
      </BotdProvider>
    </AppLighthouseProvider>
  )
}

function AppLighthouseProvider({ children }: { children: React.ReactNode }) {
  const publicApiKey = FPJS_PUBLIC_TOKEN
  const region = FPJS_REGION as 'us' | 'eu' | 'ap' | undefined
  const isLocalDev = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
  const endpoint = isLocalDev ? 'https://api.stage.fpjs.sh' : `${HOST}/r4a0Rd2Xs/`

  if (!publicApiKey) {
    // If API key is not provided, render children without FingerprintProvider
    return <>{children}</>
  }

  return (
    <FingerprintProvider
      apiKey={publicApiKey}
      region={region}
      endpoints={endpoint}
      cache={{
        storage: 'agent',
        duration: 'optimize-cost',
      }}
    >
      {children}
    </FingerprintProvider>
  )
}
